import { useEffect } from 'react';
import { reasonCopy } from './reason-copy';

/**
 * /connect/done | /connect/error — hosted terminal pages for the onboarding
 * link flow. The backend's default success/failure redirects
 * (onboarding-notify.service.ts) land here when the org admin configured no
 * custom successRedirectUrl / failureRedirectUrl. Static, public, no API calls
 * — without these routes the URL falls through to /connect/:token, which
 * treats "done" as a link token and shows "This link is no longer active."
 *
 * These pages are what an org's END-CUSTOMER sees, so they stay unbranded:
 * no HookMyApp name in the copy, and the SPA's global <title>HookMyApp</title>
 * is overridden with a neutral tab title.
 */

const center: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '100vh',
  fontFamily: 'system-ui',
  padding: 24,
  textAlign: 'center',
};

function useNeutralTitle(title: string) {
  useEffect(() => {
    document.title = title;
  }, [title]);
}

export function ConnectDonePage() {
  useNeutralTitle('Connected');
  // AIT-621 Facebook links: the default success redirect names the Page and
  // its linked Instagram account so the customer sees what just connected.
  const params = new URLSearchParams(window.location.search);
  const connected = [params.get('facebook_page'), params.get('instagram_username') ? `@${params.get('instagram_username')}` : null]
    .filter((x): x is string => !!x)
    .join(' and ');
  return (
    <div style={center}>
      <p role="status">
        {connected ? `Connected ${connected} successfully.` : 'Connected successfully.'} You can close this window.
      </p>
    </div>
  );
}

export function ConnectErrorPage() {
  useNeutralTitle('Connection failed');
  const reason = new URLSearchParams(window.location.search).get('reason');
  return (
    <div style={center}>
      <p role="alert">{reasonCopy(reason)}</p>
    </div>
  );
}
